import {useEffect, useRef, useState} from 'react';

export const ISSUE_KEY_REGEX = /^[A-Za-z][A-Za-z0-9_]+-[1-9][0-9]*$/;

export const validateIssueKeyFormat = (key: string) => ISSUE_KEY_REGEX.test(key.trim());

/**
 * Centralized validation for Jira issue keys.
 * - Validates format using ISSUE_KEY_REGEX
 * - Debounced lookup via provided searchIssues function
 */
export function useIssueValidation(
  issueKey: string,
  searchIssues: (query: string, exact?: boolean) => Promise<Array<{ key?: string }>>,
  debounceMs = 300
) {
  const [validatingIssue, setValidatingIssue] = useState(false);
  const [isIssueValid, setIsIssueValid] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    const key = (issueKey || '').trim();

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    if (!key || !validateIssueKeyFormat(key)) {
      setIsIssueValid(false);
      setValidatingIssue(false);
      return;
    }

    setValidatingIssue(true);
    const currentSeq = ++seqRef.current;
    const upperKey = key.toUpperCase();

    debounceRef.current = window.setTimeout(async () => {
      try {
        const items = await searchIssues(upperKey);
        // Consider valid if any returned result has an exact matching key (case-insensitive)
        const ok = Array.isArray(items) && items.some(it => (it?.key || '').toUpperCase() === upperKey);
        // Only apply if still latest
        if (seqRef.current === currentSeq) {
          setIsIssueValid(ok);
        }
      } catch {
        if (seqRef.current === currentSeq) {
          setIsIssueValid(false);
        }
      } finally {
        if (seqRef.current === currentSeq) {
          setValidatingIssue(false);
        }
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [issueKey, searchIssues, debounceMs]);

  return {isIssueValid, validatingIssue};
}
